import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { forwardRef } from 'react';
import styles from './Button.module.css';
import { type AllowedButtonTags, type ButtonKind, type ButtonProps, type ButtonSize, type ButtonType } from './types';
function getButtonSizeStyle(size: ButtonSize) {
  return styles[`size-${size}`];
}
export function getButtonKindStyles(kind: ButtonKind, ghost?: boolean) {
  return [{
    [styles['ghost']!]: ghost
  }, styles[`kind-${kind}`]!];
}
export function getButtonStyles({
  kind,
  size,
  ghost,
  fullWidth,
  busy,
  userClassName
}: {
  kind: ButtonKind;
  size: ButtonSize;
  ghost?: boolean;
  fullWidth?: boolean | 'mobile-only';
  busy?: boolean;
  userClassName?: string;
}) {
  return classNames(styles.button, getButtonSizeStyle(size), userClassName, ...getButtonKindStyles(kind, ghost), {
    [styles.busy!]: busy,
    [styles.idle!]: !busy,
    ['fullWidth']: fullWidth && fullWidth !== 'mobile-only',
    ['fullWidthOnMobile']: fullWidth && fullWidth === 'mobile-only'
  });
}
export function headlessButtonProps<T extends AllowedButtonTags>({
  children,
  kind = 'primary',
  size = 'default',
  'as': _ = 'button' as T,
  'className': userClassName,
  ghost,
  busy,
  id,
  'aria-disabled': ariaDisabled_,
  'aria-describedby': ariaDescribedBy_,
  disabledReason,
  disabled_,
  disabled,
  fullWidth,
  ...props
}: ButtonProps<T>) {
  const buttonDisabled = disabled || disabled_;
  const ariaDisabled = buttonDisabled || ariaDisabled_ || busy;
  const disabledReasonId = ariaDisabled && disabledReason ? `${id}-disabled-reason` : undefined;
  const ariaDescribedBy = ariaDescribedBy_ ?? disabledReasonId;
  const className = getButtonStyles({
    kind,
    size,
    ghost,
    fullWidth,
    busy,
    userClassName
  });
  const children_ = <>
      {disabledReasonId && <span id={disabledReasonId} className={styles.srOnly}>
          {disabledReason}
        </span>}
      {children}
    </>;
  return {
    className,
    'children': children_,
    'aria-disabled': ariaDisabled,
    'aria-describedby': ariaDescribedBy,
    'aria-busy': busy,
    'disabled': disabled_,
    id,
    ...props
  };
}
const Button = forwardRef(function Button(t0, ref) {
  const $ = _c(14);
  let props;
  let t1;
  if ($[0] !== t0) {
    const {
      id,
      ...t2
    } = t0;
    props = t2;
    t1 = headlessButtonProps({
      ...props,
      id
    } as ButtonProps<T>);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  let children;
  let props_;
  if ($[3] !== t1) {
    ({
      children,
      ...props_
    } = t1);
    $[3] = t1;
    $[4] = children;
    $[5] = props_;
  } else {
    children = $[4];
    props_ = $[5];
  }
  if (props.as === "a") {
    const t2 = ref as React.Ref<HTMLAnchorElement>;
    let t3;
    if ($[6] !== children || $[7] !== props_ || $[8] !== t2) {
      t3 = <a ref={t2} {...props_}>{children}</a>;
      $[6] = children;
      $[7] = props_;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  const t2 = ref as React.Ref<HTMLButtonElement>;
  let t3;
  if ($[10] !== children || $[11] !== props_ || $[12] !== t2) {
    t3 = <button ref={t2} {...props_}>{children}</button>;
    $[10] = children;
    $[11] = props_;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}) as ButtonType;
export default Button;